import { useMemo } from 'react';

import orderBy from 'lodash/orderBy';

import {
  Button,
  EllipsisWithTooltip,
  ExternalLink,
  Typography,
  Stack,
} from '@cast/design-system';
import { EdgePhase } from '@cast/types-omni';

import { makeNodeListLink } from 'features/cluster/nodes';
import { useGetEdgeLocationNodesQuery } from 'hooks/queries/omni';

import { CUSTOM_EDGE_PROVIDER } from '../../constants';
import { EdgeLocationMappedState, EnhancedEdgeLocation } from '../../types';
import { useProvisionNodesDrawer } from '../useProvisionNodesDrawer';

type EdgeLocationNodesSectionProps = {
  edgeLocation: EnhancedEdgeLocation;
  clusterId: string;
  orgId: string;
};

export const EdgeLocationNodesSection = ({
  edgeLocation,
  clusterId,
  orgId,
}: EdgeLocationNodesSectionProps) => {
  const { openProvisionNodesDrawer } = useProvisionNodesDrawer();
  const { edgeNodes } = useGetEdgeLocationNodesQuery({
    clusterId,
    edgeLocationId: edgeLocation.id!,
  });

  const edges = useMemo(() => {
    if (!edgeNodes) {
      return [];
    }

    const filteredEdges = edgeNodes.filter(
      (edge) => edge.phase !== EdgePhase.PHASE_UNSPECIFIED
    );

    return orderBy(filteredEdges, 'name');
  }, [edgeNodes]);
  const hasEdgeNodes = !!edges.length;

  return (
    <Stack gap={16} data-testid="edge-location-nodes-section">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="L12B">
          Nodes{' '}
          {hasEdgeNodes ? (
            <span data-testid="edge-location-nodes-count">
              ({edges.length})
            </span>
          ) : (
            ''
          )}
        </Typography>
        <Button
          size="sm"
          variant="secondary"
          testId="provision-nodes-button"
          disabled={
            !(
              edgeLocation.mappedState === EdgeLocationMappedState.Ready ||
              edgeLocation.mappedState === EdgeLocationMappedState.InUse
            )
          }
          onClick={() => openProvisionNodesDrawer({ edgeLocation, clusterId })}
        >
          Provision edge node
        </Button>
      </Stack>
      {hasEdgeNodes ? (
        <Stack gap={16} data-testid="edge-location-nodes-list">
          {edges.map((node) => (
            <ExternalLink
              key={node.id}
              sx={{ typography: 'P14R', minWidth: 0 }}
              link={makeNodeListLink(clusterId, {
                orgId,
                nodeNames: [node.name],
              })}
            >
              <EllipsisWithTooltip TypographyProps={{ variant: 'P14R' }}>
                {node.name}
              </EllipsisWithTooltip>
            </ExternalLink>
          ))}
        </Stack>
      ) : (
        <Typography variant="P14R" color="text.secondary">
          {edgeLocation.provider === CUSTOM_EDGE_PROVIDER
            ? "No nodes yet. This edge location isn't managed by the Autoscaler. Use the Provision edge node button above or the API to provision the script and run it on your target machine."
            : 'No nodes yet. The Autoscaler will provision nodes here as needed, or you can provision one manually.'}
        </Typography>
      )}
    </Stack>
  );
};
